/**
 * The Share control (FRONT-1880, spec Decision 5 and the revised Decision 8).
 *
 * ONE decision surface (`ShareControlBody`) behind three shells, because the
 * three places a person reaches it from want different framing and none of
 * them may disagree about the app: a POPOVER beside the coding session's
 * audience badge, the same popover in Apps → Edit → Sharing where the
 * Visibility combobox used to be, and a DIALOG from the Apps row menu's
 * "Share…" — a menu item that used to navigate to the editor and leave the
 * person to find the section themselves, which is a redirect, not sharing.
 *
 * **One question, one answer.** Before this, an app had three overlapping
 * doors: the audience, a `?share=<token>` link, and an allowlist that only
 * narrowed the token. The allowlist did nothing at all on the app's own url,
 * because the access gate never read it — so a named colleague clicking the
 * ordinary link got the 403 page. Now the audience IS the answer: Only you,
 * Shared (the whole workspace, or people you name), Public. The gate honours
 * the named list (`src/apps/access.ts`), so what the control says and what
 * the link does are the same thing.
 *
 * **Public is loud, not modal** (Decision 6). It renders in the warning tone
 * and states its consequence in full where the choice is made, with no
 * confirm dialog: it is reversible in one click, and a modal on every
 * publish taxes the people doing it deliberately.
 *
 * **Availability and its reason are ONE value, and it comes from the
 * server.** `audienceLock` is `audienceLockReason` (`src/apps/audience.ts`)
 * computed for this viewer; the choices are disabled exactly when it is a
 * string, and that same string is rendered as visible text — never only a
 * tooltip, which says nothing on a touch screen. The audience route refuses
 * with the identical sentence, so the screen and the server cannot tell the
 * person two different stories, and a fifth reason cannot be added without
 * writing the words for it.
 *
 * The body takes no padding and sets no width: each shell frames it, so the
 * dialog looks like every other dialog in the app and the popover like every
 * other popover, rather than one of them wearing the other's shape.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  CaretDownIcon,
  CheckIcon,
  GlobeIcon,
  LinkSimpleIcon,
  LockIcon,
  UsersThreeIcon,
  WarningIcon,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { publishUndoToast } from './publish-undo';
import { api } from '~/lib/api-client';
import { cn } from '~/lib/utils';
import { Button } from '~/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '~/components/ui/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '~/components/ui/tooltip';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '~/components/ui/dialog';
import { useAuthMe } from '~/hooks/queries/use-is-admin';
import { appAudience, APP_AUDIENCE_COPY, type AppAudience } from '~/features/coding/coding-events';
import { SharePeopleInput, isValidEntry } from './share-people-input';
import { PublicListingToggle, showsUnlistedTag, UnlistedTag, UNLISTED_TAG_LABEL } from './public-listing-toggle';

/** Fixed order, safest first — the list reads as a ramp outwards. */
export const SHARE_AUDIENCE_ORDER: AppAudience[] = ['only-you', 'shared', 'public'];

/** The two shapes "Shared" can take. Mirrors `ShareScope` in `src/apps/audience.ts`. */
export type ShareScope = 'workspace' | 'people';

export const AUDIENCE_ICON: Record<AppAudience, typeof LockIcon> = {
  'only-you': LockIcon,
  shared: UsersThreeIcon,
  public: GlobeIcon,
};

/**
 * The colour each audience's glyph carries, as a three-step ramp on how far
 * the app travels: private keeps the inherited chrome colour, shared is
 * yellow, public is red.
 *
 * ONE map, read by both the trigger and the picker rows, because they are two
 * views of the same fact — a person picks "Public" in the list and then reads
 * the lock afterwards, and two colour decisions for one meaning would drift
 * the first time either is touched.
 *
 * Both tones are the repo's own tokens rather than raw Tailwind scales
 * (CLAUDE.md: one red, one amber, and raw scales are not theme-aware), softened
 * with opacity to land on a pastel: at full strength the pair shouted, which is
 * wrong for a state the person chose on purpose rather than an error.
 */
export const AUDIENCE_ICON_CLASS: Record<AppAudience, string | undefined> = {
  'only-you': undefined,
  shared: 'text-amber-300/80',
  public: 'text-destructive/55',
};

/** "1 colleague" / "N colleagues" — the toast copy's one pluralization, so
 *  a future count-word tweak lands in one place instead of drifting between
 *  the share and re-share toasts (FRONT-1926 review). */
function colleagueCount(n: number): string {
  return n === 1 ? '1 colleague' : `${n} colleagues`;
}

/**
 * What Public actually costs, spelled out at the point of choice rather
 * than behind a confirmation. Longer than `APP_AUDIENCE_COPY`'s one-line
 * hint on purpose: the hint labels a state a user is reading, this sentence
 * is what they are agreeing to.
 *
 * FRONT-1775 (PR 3) forced every served app to `noindex` and removed the
 * SEO tab's "Allow indexing" field that used to let a live public app opt
 * out — so this sentence can now promise what FRONT-1880's review found it
 * couldn't yet.
 */
const PUBLIC_CONSEQUENCE =
  'Anyone with the link opens it without signing in, inside or outside your company. Search engines are told not to index it, and nothing outside EDDY lists it.';

/**
 * Why the only remaining colleague cannot be removed. Rendered as VISIBLE
 * text beside the chips as well as carried on the disabled control: a reason
 * that lives only in an `aria-label` is a reason nobody reads.
 */
const LAST_PERSON_LOCKED =
  'This is the only colleague it is shared with. Choose \u201cOnly you\u201d above to close the app instead.';

/**
 * The popover's frame. Bounded to the space Radix reports as available and
 * then scrolls: unbounded, the Public row fell off the bottom of the screen
 * in the app editor, where this opens low in a long accordion — and the one
 * state that most needs reading was the one clipped.
 */
export const SHARE_POPOVER_CLASS =
  'w-[360px] max-h-[min(75vh,var(--radix-popover-content-available-height))] overflow-y-auto p-3.5';

/**
 * The dialog's frame. `[&>*]:min-w-0` is load-bearing, not tidying:
 * `DialogContent` is a CSS GRID, and a grid item's default `min-width: auto`
 * refuses to shrink below its content's max-content width. The app's url is
 * one unbreakable string, so without this the body grew past the panel and
 * painted its rows ~50px outside it — the "still broken" render Marko hit on
 * 2026-09-09. The body sets `min-w-0` on itself too, for the popover's flex
 * column, which has the same default.
 */
export const SHARE_DIALOG_CLASS = 'sm:max-w-[400px] max-h-[85vh] overflow-y-auto [&>*]:min-w-0';

/**
 * Every change in here raises a toast, and the toaster is portalled to
 * `<body>` — so dismissing a toast counted as a click OUTSIDE the control
 * and closed the whole thing mid-decision. Radix cannot know the toaster is
 * ours; this tells it.
 *
 * Narrow on purpose: only Sonner's container and Radix's own popper layers
 * are exempt, so a click anywhere else still closes the control as an
 * outside click should.
 */
export function dismissUnlessToast(event: {
  target: EventTarget | null;
  preventDefault: () => void;
}) {
  const target = event.target as Element | null;
  if (!target?.closest) return;
  if (target.closest('[data-sonner-toaster], [data-radix-popper-content-wrapper]')) {
    event.preventDefault();
  }
}

/** Everything a shell needs about the app it is sharing. */
export interface ShareTarget {
  slug: string;
  title?: string;
  /** Who can open it now, from the one shared rule. */
  audience: AppAudience;
  /** Which shape "Shared" currently has, read off the row server-side. */
  scope: ShareScope;
  /** The people named on it, when the scope is `people`. */
  people: string[];
  /** FRONT-1926: the people remembered from a share "Only you" paused rather
   *  than deleted — `[]` when there is none. Never the same as `people`
   *  while the audience is `only-you`: that field is the ACTIVE grant, this
   *  is what "Shared -> Only specific colleagues" can offer to restore. */
  pausedPeople: string[];
  /** The app's own URL, absolute where the server knew the origin. */
  url: string;
  /** Why the choices are unavailable to this viewer, or null when they are not. */
  audienceLock: string | null;
  /**
   * FRONT-2091: is the app reachable by anyone but its owner RIGHT NOW
   * (`status === 'live'`)?
   *
   * Required, not optional, and deliberately so. `audience` stopped
   * answering this the moment the lifecycle collapse came out of
   * `appAudience`: "Shared" now means a colleague WILL be able to open it,
   * and only this field says whether they can yet. An optional flag would
   * default a mount that forgot it to "live", i.e. to the confident claim —
   * the wrong direction for missing data. Required means the compiler
   * enumerates every mount instead.
   *
   * Only `whoOpensTheLink` reads it: the three audience CHOICES are a
   * decision the owner makes regardless of lifecycle (that is the whole
   * point of the split), so nothing here is gated on it.
   */
  live: boolean;
  /**
   * FRONT-2566: do colleagues find it in their lists? Only meaningful while
   * the audience is Public. Required for the same reason as `live`: a mount
   * that forgot it must fail to compile, never quietly show a switch whose
   * position is a guess.
   */
  listed: boolean;
}

/**
 * The one place a sharing decision is sent. Every shell calls this, so the
 * request shape, the toast and the error handling cannot drift between them.
 */
function useShareWriter(target: ShareTarget | null, onChanged?: () => void) {
  const [pending, setPending] = useState<AppAudience | null>(null);
  const qc = useQueryClient();
  const me = useAuthMe();
  const ownEmail = me.data?.email ?? null;
  const ownDomain =
    ownEmail && ownEmail.includes('@') ? ownEmail.slice(ownEmail.lastIndexOf('@') + 1) : null;

  async function apply(
    next: { audience: AppAudience; scope?: ShareScope; people?: string[]; listed?: boolean },
    announce: string,
  ): Promise<boolean> {
    if (!target || target.audienceLock !== null || pending) return false;
    setPending(next.audience);
    try {
      // An offline app that this makes live is published here, and the answer names that publish.
      const answer = await api.post<{ publishId?: number }>(`/api/admin/apps/${target.slug}/audience`, next);
      toast.success(announce, publishUndoToast(qc, target.slug, answer));
      onChanged?.();
      return true;
    } catch (err) {
      // The server's refusal is the same sentence this control would have
      // shown greyed out, so show it verbatim rather than a generic failure.
      toast.error((err as Error).message || 'Could not change who can open this app');
      return false;
    } finally {
      setPending(null);
    }
  }

  return { pending, ownDomain, apply };
}

/**
 * Copy-to-clipboard with the insecure-context case handled honestly. Plain
 * function rather than a hook (FRONT-2103) — the Apps admin list needs the
 * same behaviour once per ROW inside a `.map()`, where a `useState`-backed
 * hook can't be called (the call count would vary with the list length).
 * Returns whether it actually copied; the caller decides how to reflect
 * that (a hook's local `copied` state, a list's per-row `copiedSlug`, …).
 */
export async function copyTextToClipboard(text: string): Promise<boolean> {
  // Guard on the API being present rather than `?.` — in an insecure
  // context `navigator.clipboard` is undefined and an optional call would
  // resolve to `await undefined`, flashing success with nothing copied.
  if (!navigator.clipboard) {
    toast.error('Copying needs a secure (https) context');
    return false;
  }
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // FRONT-2354 fix-round F8: "the link" was accurate when this helper had
    // one caller (a share link); it now also backs the machine settings
    // panel's two command-copy buttons, where "link" is simply wrong.
    toast.error('Could not copy to the clipboard');
    return false;
  }
}

/**
 * Copy `url` to the clipboard and flip `copied` true for 1.6s. The
 * insecure-context/failure handling itself lives in `copyTextToClipboard`
 * above (code-review finding, FRONT-2103) — this hook only owns the
 * component-local "show a checkmark" state on top of it.
 */
function useCopyLink(url: string) {
  const [copied, setCopied] = useState(false);
  // Review finding (PR #1459): a rapid re-click let the FIRST click's timer
  // clear `copied` early — the exact race fixed in the list's own copy
  // button (`copySlugLink`, apps-admin.tsx) via the same tracked-timeout
  // pattern. Clearing any pending timer before starting a new one means
  // every click restarts the full 1.6s window instead of racing the last.
  const timeoutRef = useRef<number | null>(null);
  async function copyLink() {
    if (await copyTextToClipboard(url)) {
      if (timeoutRef.current !== null) window.clearTimeout(timeoutRef.current);
      setCopied(true);
      timeoutRef.current = window.setTimeout(() => {
        setCopied(false);
        timeoutRef.current = null;
      }, 1600);
    }
  }
  return { copied, copyLink };
}

/**
 * Which Shared sub-choice is MARKED, or `null` for neither (FRONT-1880,
 * second review 2026-09-09).
 *
 * Null is a real state, not a missing value. When Shared is merely being
 * explored on an app that is not shared yet, nothing has been written, so
 * nothing may read as chosen: the first version dotted "Everyone in your
 * workspace" by defaulting to the server's scope — beside a Shared row that
 * was itself unchecked and a link line still saying only the owner could
 * open it. A person closing the popover there believed they had shared the
 * app. Only a scope the server holds (the app IS shared) or one the person
 * has clicked (a draft) is ever marked. Pure and exported so the rule has
 * its own test; the hook's state lives in a shell no DOM-less test can click.
 */
export function markedScope(
  draft: { scope: ShareScope | null } | null,
  target: Pick<ShareTarget, 'audience' | 'scope'> | null,
): ShareScope | null {
  if (draft) return draft.scope;
  if (target?.audience === 'shared') return target.scope;
  return null;
}

/**
 * The shared behaviour behind every shell: what each choice sends, and what
 * the person is told afterwards. Extracted so the popover and the dialog
 * cannot end up writing two different requests for the same click.
 */
function useShareControl(target: ShareTarget | null, onChanged?: () => void) {
  const { pending, ownDomain, apply } = useShareWriter(target, onChanged);
  const { copied, copyLink } = useCopyLink(target?.url ?? '');
  /**
   * What the person is LOOKING at, which can be ahead of what is stored —
   * and it is keyed by SLUG.
   *
   * Keyed, because the row menu's dialog is mounted once per page and only
   * its `app` prop changes: an un-keyed draft from the app you looked at a
   * moment ago described the next one, so a workspace-shared app could open
   * reading "Only specific colleagues" (found while reviewing this branch).
   * Ignoring a draft belonging to another slug is a rule rather than an
   * effect, so there is no reset to forget and nothing to remount.
   */
  const [draft, setDraft] = useState<{
    slug: string;
    scope: ShareScope | null;
  } | null>(null);
  const mine = draft && draft.slug === target?.slug ? draft : null;
  const scope = markedScope(mine, target);
  // The draft carried the audience being LOOKED at too, until FRONT-2134 put
  // the sub-choice on screen at every audience — there is no reveal left for
  // it to drive, so it went rather than sitting here unread. What it was
  // really protecting (FRONT-1880 review) lives in the two choosers below:
  // the previous version posted "the whole workspace" the moment Shared was
  // clicked, so getting to "name two colleagues" exposed the app to every
  // colleague on the way. Picking Shared still writes nothing.
  const people = target?.people ?? [];
  const pausedPeople = target?.pausedPeople ?? [];
  function show(nextScope: ShareScope | null) {
    if (target) setDraft({ slug: target.slug, scope: nextScope });
  }

  return {
    pending,
    ownDomain,
    copied,
    copyLink,
    scope,
    people,
    pausedPeople,
    /**
     * Pick an audience. **Shared writes nothing on its own** — it reveals
     * the sub-choice, and the sub-choice does the writing. Exposure must
     * never be a side effect of exploring the options.
     */
    async chooseAudience(next: AppAudience) {
      if (!target) return;
      if (next === 'shared') {
        // Already shared: mark the scope the server holds. Not yet shared:
        // mark NOTHING — the sub-choice is a question, and a pre-filled
        // answer reads as a state the app is not in. Same rule as the
        // initial render, so exploring and reopening cannot disagree.
        show(markedScope(null, target));
        return;
      }
      // Nothing is marked at an audience that is not Shared. The draft used
      // to carry the audience too, so a leftover scope was merely invisible
      // here; with the sub-choice on screen at every audience (FRONT-2134) it
      // would paint "Only specific colleagues" as CHECKED under a freshly
      // private app — and pop FRONT-1926's "share again with the same 2
      // colleagues?" offer beside a link line reading "Only you can open this
      // link". Clearing it is the same rule as the Shared branch above: mark
      // only what the server holds or the person has clicked.
      show(null);
      if (next === target.audience) return;
      await apply(
        { audience: next },
        next === 'public' ? 'Anyone with the link can open it' : 'Only you can open it now',
      );
    },
    /**
     * FRONT-2566: list or unlist a Public app for colleagues. Rides the same
     * audience write with Public re-applied, so access and listing are one
     * decision in one request.
     */
    async setListed(next: boolean) {
      if (!target || target.audience !== 'public') return;
      await apply(
        { audience: 'public', listed: next },
        next ? 'Colleagues can find it in Apps again' : 'Hidden from colleagues\u2019 lists. The link still works.',
      );
    },
    /** Switch between the whole workspace and named colleagues. */
    async chooseScope(next: ShareScope) {
      if (!target) return;
      show(next);
      if (next === 'people') {
        // Nothing to write yet — the first colleague added is what makes
        // this a real state, and the server refuses an empty named share.
        if (people.length === 0) return;
        await apply(
          { audience: 'shared', scope: 'people', people },
          `Shared with ${colleagueCount(people.length)}`,
        );
        return;
      }
      await apply({ audience: 'shared', scope: 'workspace' }, 'Shared with your workspace');
    },
    async addPerson(entry: string) {
      const next = [...people, entry];
      await apply({ audience: 'shared', scope: 'people', people: next }, `Shared with ${entry}`);
    },
    /**
     * FRONT-1926: re-apply the list "Only you" paused rather than deleted —
     * the explicit act that re-admits people who were previously named. Never
     * fired automatically: `chooseScope` only reveals the offer, it does not
     * take it.
     */
    async restorePausedShare() {
      await apply(
        { audience: 'shared', scope: 'people', people: pausedPeople },
        `Shared again with ${colleagueCount(pausedPeople.filter(isValidEntry).length)}`,
      );
    },
    async removePerson(entry: string) {
      const next = people.filter((p) => p !== entry);
      // The last one is not removable here (the chip says why) — this guard
      // is the second half of that, so a stale render cannot send an empty
      // named share the server would refuse.
      if (next.length === 0) return;
      await apply(
        { audience: 'shared', scope: 'people', people: next },
        `${entry} can no longer open it`,
      );
    },
  };
}

export interface AppSharePopoverProps {
  slug: string;
  audience: AppAudience;
  scope?: ShareScope;
  people?: string[];
  /** FRONT-1926: the people remembered from a share "Only you" paused. */
  pausedPeople?: string[];
  url: string;
  audienceLock: string | null;
  /** FRONT-2091: reachable by anyone but the owner right now. Required —
   *  see `ShareTarget.live` for why it is not defaulted. */
  live: boolean;
  /** FRONT-2566: do colleagues find it in their lists? Required — see `ShareTarget.listed`. */
  listed: boolean;
  onChanged?: () => void;
  /** `compact` is a gallery row's inline chip (sketches, decks); `full` the
   *  Sharing tab and the coding session header's controls row. */
  size?: 'compact' | 'full';
  /**
   * What the button SAYS. `action` reads "Share" — in the coding header it
   * sits in the controls row while the audience badge it pairs with states
   * the current audience on the readout row below, so this is the verb and
   * the badge is the value. `audience` reads the audience itself with a caret,
   * for the Sharing tab: it replaced a combobox, and a combobox shows its
   * current value. Labelling that one "Share" made it read as an action on
   * an app that is already shared, which is what it is not.
   *
   * `icon` (FRONT-2258) is the lock GLYPH alone, for the coding session's
   * one-row header: the three audiences already have three distinct locks
   * (closed, open shackle, globe), so the word beside them said a second
   * time what the icon says. It is the one shape that MUST carry a tooltip —
   * an icon with no label and no hover text is a control nobody can name.
   */
  trigger?: 'action' | 'audience' | 'icon';
  className?: string;
}

export function AppSharePopover({
  slug,
  audience,
  scope = 'workspace',
  people = [],
  pausedPeople = [],
  url,
  audienceLock,
  live,
  listed,
  onChanged,
  size = 'full',
  trigger = 'action',
  className,
}: AppSharePopoverProps) {
  const [open, setOpen] = useState(false);
  const target: ShareTarget = { slug, audience, scope, people, pausedPeople, url, audienceLock, live, listed };
  const control = useShareControl(target, onChanged);
  const TriggerIcon = AUDIENCE_ICON[audience];
  // FRONT-2566: every mount of this popover is an owner surface (the Sharing
  // tab, the coding header, a gallery chip shown only to the owner), so the
  // owner reads the listing state on the trigger itself.
  const unlisted = showsUnlistedTag({ audience, listedToColleagues: listed });

  return (
    <Popover open={open} onOpenChange={setOpen}>
      {/* FRONT-2258: the icon-only shape needs a hover hint, and `Button`'s
          own `tooltip` prop CANNOT deliver it here. That prop wraps the
          button in `Tip`, a plain function component — and this button is
          already the child of `PopoverTrigger asChild`, so Radix clones
          `Tip` and hands it the trigger's props (onClick, ref, aria-*),
          which `Tip` reads none of and silently drops. Observed exactly as
          tip.tsx's docblock predicts: the lock rendered with its correct
          accessible name and no tooltip ever opened.

          The documented composition instead — `Tooltip` OUTERMOST,
          `TooltipTrigger asChild` wrapping `PopoverTrigger asChild`, both
          genuine Radix Slots, so props merge all the way down to the button.
          Same shape as note-row.tsx's Move control. */}
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size={size === 'compact' ? 'xs' : 'sm'}
              // ONE accessible name across every shell, whatever the visible
              // label says — it names the verb AND the current state, so a
              // screen-reader user is never told only "Share" about an app that
              // is already public.
              aria-label={`Change who can open this app — currently ${APP_AUDIENCE_COPY[audience].label}${
                unlisted ? `, ${UNLISTED_TAG_LABEL.toLowerCase()} for colleagues` : ''
              }`}
              className={cn(
                'gap-1.5 active:scale-[0.96]',
                // FRONT-2267: the icon shape sits in a row of hand-rolled icon
                // buttons (Preview, Save, "…") that are a bordered box and
                // nothing else. `Button`'s outline variant brings a surface of
                // its own — `bg-background`, `shadow-xs`, and in dark mode
                // `bg-input/30` plus a different border colour — so the lock
                // read as a different KIND of control sitting between them.
                // Neutralised here rather than at the call site, so every
                // icon-shaped use of this control matches its neighbours.
                trigger === 'icon' &&
                  'bg-transparent shadow-none border-border hover:bg-muted ' +
                    'dark:bg-transparent dark:border-border dark:hover:bg-muted ' +
                    'text-muted-foreground hover:text-foreground',
                className,
              )}
            >
              {/* Colour comes from the shared ramp above — the icon is the
                  only thing that says the audience in the icon-only shape. */}
              <TriggerIcon
                size={trigger === 'icon' ? 16 : size === 'compact' ? 12 : 14}
                weight="fill"
                className={AUDIENCE_ICON_CLASS[audience]}
              />
              <TriggerLabel trigger={trigger} audience={audience} unlisted={unlisted} />
            </Button>
          </PopoverTrigger>
        </TooltipTrigger>
        {trigger === 'icon' && (
          // The hint IS the label for a control with no words: the current
          // audience as a sentence, plus what clicking does, since the glyph
          // alone can say neither.
          <TooltipContent>{`${APP_AUDIENCE_COPY[audience].hint}${
            unlisted ? ' Unlisted: colleagues do not find it in their lists.' : ''
          } Click to change that.`}</TooltipContent>
        )}
      </Tooltip>
      <PopoverContent
        align="end"
        collisionPadding={12}
        className={SHARE_POPOVER_CLASS}
        onInteractOutside={dismissUnlessToast}
      >
        <ShareControlBody target={target} control={control} heading="Who can open this app" />
      </PopoverContent>
    </Popover>
  );
}

/** The words beside the trigger's lock: none for the icon shape, the audience
 *  with a caret for the Sharing tab, "Share" elsewhere — plus the owner's
 *  "Unlisted" tag (FRONT-2566) on the two shapes that have words. */
function TriggerLabel({
  trigger,
  audience,
  unlisted,
}: {
  trigger: NonNullable<AppSharePopoverProps['trigger']>;
  audience: AppAudience;
  unlisted: boolean;
}) {
  if (trigger === 'icon') return null;
  if (trigger === 'audience') {
    return (
      <>
        {APP_AUDIENCE_COPY[audience].label}
        {unlisted && <UnlistedTag />}
        <CaretDownIcon size={11} weight="bold" className="text-muted-foreground" />
      </>
    );
  }
  return (
    <>
      Share
      {unlisted && <UnlistedTag />}
    </>
  );
}

/**
 * The same control as a DIALOG, for the Apps row menu's "Share…".
 *
 * A menu item cannot anchor a popover — the menu unmounts on select and
 * takes any popover inside it along — so this shell is a dialog. Radix's
 * own `Dialog`, framed like every other dialog in the app (its own padding,
 * a real visible title), rather than a popover body wedged into a
 * zero-padding content box.
 */
export function AppShareDialog({
  app,
  open,
  onOpenChange,
  onChanged,
}: {
  app: ShareTarget | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onChanged?: () => void;
}) {
  const control = useShareControl(app, onChanged);
  return (
    <Dialog open={open && !!app} onOpenChange={onOpenChange}>
      <DialogContent className={SHARE_DIALOG_CLASS} onInteractOutside={dismissUnlessToast}>
        <DialogHeader>
          <DialogTitle className="text-sm">
            Who can open {app?.title ? `“${app.title}”` : 'this app'}
          </DialogTitle>
        </DialogHeader>
        {app && <ShareControlBody target={app} control={control} />}
      </DialogContent>
    </Dialog>
  );
}

/**
 * The decision surface itself — presentational, and the only place the three
 * audiences and their copy are laid out.
 *
 * Exported for its own test: this suite runs under plain node with no DOM,
 * and Radix never renders a CLOSED popover's content, so a test that mounted
 * only a shell would assert the trigger and guard none of the words below.
 *
 * No outer padding and no width: the shell frames it. `heading` is optional
 * because the dialog already has a real title above it, and printing a
 * second one would be the same duplication the Sharing tab's old
 * "Currently Public" line was.
 */
export function ShareControlBody({
  target,
  control,
  heading,
}: {
  target: ShareTarget;
  control: ReturnType<typeof useShareControl>;
  heading?: string;
}) {
  const locked = target.audienceLock !== null;
  // The widen-confirm lives HERE rather than in `SharedScopeChoice`, because
  // the thing that must close it — picking another audience — is a click on
  // one of the rows below, not a value the sub-choice can watch.
  const [confirmWiden, setConfirmWiden] = useState(false);
  const busy = control.pending !== null;
  // FRONT-2566: listing only exists once the app IS Public — revealed after
  // the choice, unlike the Shared sub-choice, because it qualifies a state
  // rather than completing one.
  const listingBelow = (option: AppAudience) =>
    option === 'public' && target.audience === 'public' ? (
      <PublicListingToggle
        listed={target.listed}
        disabled={locked || busy}
        onChange={(next) => void control.setListed(next)}
      />
    ) : null;

  return (
    // `min-w-0` for the same reason the dialog sets it on its children: the
    // app's url below is one unbreakable string, and an auto-min-width
    // grid/flex item grows to fit it rather than letting it truncate.
    <div className="space-y-3 min-w-0">
      <div>
        {heading && <p className="text-sm font-medium">{heading}</p>}
        {locked ? (
          // Visible text, not a tooltip: this is the reason the person has
          // to act on, and a hover hint reaches nobody on a phone.
          <p className={cn('text-xs text-muted-foreground', heading && 'mt-1')}>
            {target.audienceLock}
          </p>
        ) : (
          <p className={cn('text-xs text-muted-foreground', heading && 'mt-1')}>
            Changes apply straight away. You can change this back at any time.
          </p>
        )}
      </div>

      <ShareAudienceOptions
        label="Who can open this app"
        options={SHARE_AUDIENCE_ORDER.map((option) => ({
          key: option,
          label: APP_AUDIENCE_COPY[option].label,
          hint: option === 'public' ? PUBLIC_CONSEQUENCE : APP_AUDIENCE_COPY[option].hint,
          icon: AUDIENCE_ICON[option],
          iconClass: AUDIENCE_ICON_CLASS[option],
          warning: option === 'public',
        }))}
        selected={target.audience}
        locked={locked}
        pending={control.pending}
        onPick={(option) => {
          // Picking ANY audience answers the widen-confirm by abandoning it.
          // Done on the click rather than by watching a value change
          // (DerStoffel, PR 1480): from an Only-you app with a PAUSED list,
          // opening the confirm writes nothing, so the marked scope is null
          // before and after — an effect keyed on it never re-runs, and the
          // amber panel survived onto a Public app with its "Share with
          // everyone" button still live.
          setConfirmWiden(false);
          void control.chooseAudience(option);
        }}
        renderBelow={(option) =>
          // The Shared sub-choice, nested under Shared rather than offered as
          // a fourth audience: everyone who reads a badge still sees one of
          // three words, and an app shared with two colleagues is Shared, not
          // a state nobody has a word for.
          //
          // Shown at EVERY audience, not just once Shared is the one being
          // looked at (FRONT-2134): collapsed, the three rows read as the
          // whole decision and nobody learns that per-colleague sharing
          // exists at all. What keeps exploring safe was never the hiding —
          // it is that neither radio is marked until one is clicked, and that
          // `chooseScope` is the only thing that writes. Both unchanged.
          option === 'shared' && !locked ? (
            <SharedScopeChoice
              control={control}
              busy={busy}
              confirmWiden={confirmWiden}
              setConfirmWiden={setConfirmWiden}
            />
          ) : (
            listingBelow(option)
          )
        }
      />

      <AppLinkRow target={target} control={control} />
    </div>
  );
}

/** One row of {@link ShareAudienceOptions}. */
export interface ShareAudienceOption<K extends string> {
  key: K;
  label: string;
  hint: string;
  icon: typeof LockIcon;
  /** The glyph's tone on the private → shared → public ramp. */
  iconClass?: string;
  /** Adds the warning glyph beside the label (Public). */
  warning?: boolean;
  /** Visible but not pickable; its `hint` must say why. */
  disabled?: boolean;
}

/**
 * The audience rows themselves — the one layout every share surface uses
 * (FRONT-2065): apps, slides and sketches through `ShareControlBody`, custom
 * agents through `AgentSharePopover`. Model-agnostic on purpose: the caller
 * owns what each key means and what picking one writes, so an agent's
 * private / restricted / org can wear the same rows as an app's audiences
 * without pretending to be one.
 */
export function ShareAudienceOptions<K extends string>({
  label,
  options,
  selected,
  locked,
  pending,
  onPick,
  renderBelow,
}: {
  label: string;
  options: ShareAudienceOption<K>[];
  selected: K;
  locked: boolean;
  /** The key being written right now, or null. */
  pending: K | null;
  onPick: (key: K) => void;
  renderBelow?: (key: K) => ReactNode;
}) {
  const busy = pending !== null;
  return (
    <div role="radiogroup" aria-label={label} className="-mx-1.5">
      {options.map((option) => {
        const Icon = option.icon;
        const isSelected = option.key === selected;
        const unavailable = locked || busy || !!option.disabled;
        return (
          <div key={option.key}>
            <button
              type="button"
              role="radio"
              aria-checked={isSelected}
              disabled={unavailable}
              onClick={() => onPick(option.key)}
              className={cn(
                'w-full flex items-start gap-2.5 rounded-md px-1.5 py-2 text-left transition-colors',
                'disabled:opacity-60 disabled:cursor-not-allowed',
                !unavailable && 'hover:bg-muted cursor-pointer',
                isSelected && 'bg-muted/60',
              )}
            >
              {/* Same ramp as the trigger, from the same map — this row is
                  where the person CHOOSES the audience and the lock is
                  where they read it back, so the two must agree. */}
              <Icon
                size={15}
                weight="fill"
                className={cn('mt-0.5 shrink-0', option.iconClass ?? 'text-muted-foreground')}
              />
              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-1.5">
                  {/* The LABEL stays ordinary text in every row. Colour does
                      one job here — it is on the glyph — and a tinted label
                      both weakened the contrast of the one word the person
                      is reading and made "Public" look like a warning about
                      a state they chose deliberately. */}
                  <span className="text-sm font-medium">{option.label}</span>
                  {option.warning && (
                    <WarningIcon size={12} weight="fill" className={AUDIENCE_ICON_CLASS.public} />
                  )}
                  {pending === option.key && (
                    <span className="text-xs text-muted-foreground">saving…</span>
                  )}
                </span>
                <span className="mt-0.5 block text-xs text-muted-foreground">{option.hint}</span>
              </span>
              <CheckIcon
                size={14}
                weight="bold"
                className={cn('mt-1 shrink-0', isSelected ? 'opacity-100' : 'opacity-0')}
              />
            </button>
            {renderBelow?.(option.key)}
          </div>
        );
      })}
    </div>
  );
}

/**
 * The Shared sub-choice — everyone in the workspace, or named colleagues —
 * with the widen-confirm and the people input that belong to it. Split out of
 * `ShareControlBody` (PR review, 2026-09-10): it is the one part of the
 * control with its own interaction state, and the body reads as three rows
 * plus a link once it is gone.
 */
function SharedScopeChoice({
  control,
  busy,
  confirmWiden,
  setConfirmWiden,
}: {
  control: ReturnType<typeof useShareControl>;
  busy: boolean;
  /** Owned by `ShareControlBody` — the audience rows up there close it. */
  confirmWiden: boolean;
  setConfirmWiden: (open: boolean) => void;
}) {
  // FRONT-1926: once "Start over" is clicked, the plain input replaces the
  // offer for as long as THIS paused list is the one being explored.
  const [startFresh, setStartFresh] = useState(false);
  const pausedPeople = control.pausedPeople ?? [];
  // A later, DIFFERENT pause (share with A, close, start over, share with B,
  // close again) is a new offer and must not stay suppressed by an earlier
  // "Start over" click made about A — reviewed 2026-09-10. Keyed on the
  // paused list's own content, not the popover's mount: this popover can
  // stay open across more than one pause/reshare cycle.
  const pausedKey = pausedPeople.join(' ');
  useEffect(() => {
    setStartFresh(false);
  }, [pausedKey]);
  // Offer the remembered list only while there is nothing active to show —
  // once a name is added the active list IS the state, and re-showing the
  // offer beside it would ask the same question twice.
  // Only email entries count (FRONT-2756): a paused list of bare words alone
  // would restore a share that opens the app to nobody, so it is not offered.
  const pausedColleagues = pausedPeople.filter(isValidEntry).length;
  const offerRestore =
    control.scope === 'people' && control.people.length === 0 && pausedColleagues > 0 && !startFresh;
  // Whichever list widening to the whole workspace would fold in / discard —
  // ACTIVE and paused can never both be non-empty at once (a paused list is
  // exactly what "only-you" leaves behind when there is no active one).
  const namesAtStake = control.people.length > 0 ? control.people.length : pausedPeople.length;
  return (
                <div className="ml-[26px] mr-1.5 mb-1 space-y-1.5 border-l border-border pl-3">
                  {control.scope === null && (
                    // Nothing is written until one of the two below is
                    // picked, and nothing below is marked — so say what the
                    // person is looking at, or the unchecked Shared row and
                    // the "only you" link line underneath read as a glitch.
                    <p className="text-xs text-muted-foreground">
                      Choose who — nothing changes until you pick one.
                    </p>
                  )}
                  {/* Their own group: nested inside the audience radiogroup
                      they would be answers four and five to "who can open
                      this app", and a screen reader would say "2 of 5" for a
                      question that has two options. Always rendered now, so
                      this is the permanent shape rather than an expanded
                      one. */}
                  <div role="radiogroup" aria-label="How it is shared">
                  {(['workspace', 'people'] as const).map((sub) => (
                    <button
                      key={sub}
                      type="button"
                      role="radio"
                      aria-checked={control.scope === sub}
                      disabled={busy}
                      onClick={() => {
                        // Widening away from named colleagues DELETES that
                        // list on the server, and the two radios sit one
                        // above the other — a misclick lost the names and
                        // opened the app to the whole org in one go
                        // (FRONT-1880 review). Ask once, inline.
                        //
                        // FRONT-1926 (code-review finding): a PAUSED list is
                        // just as real to discard as an active one — from an
                        // only-you app with nothing active, `control.scope`
                        // is null (nothing marked yet), so the ACTIVE-only
                        // check above would let this radio through with zero
                        // warning and silently clear the paused names for
                        // good. `namesAtStake` covers both.
                        if (sub === 'workspace' && namesAtStake > 0) {
                          setConfirmWiden(true);
                          return;
                        }
                        void control.chooseScope(sub);
                      }}
                      className={cn(
                        'w-full flex items-center gap-2 rounded-md px-1.5 py-1 text-left text-xs transition-colors',
                        'disabled:opacity-60 disabled:cursor-not-allowed',
                        !busy && 'hover:bg-muted cursor-pointer',
                      )}
                    >
                      <span
                        className={cn(
                          'size-3 rounded-full border shrink-0',
                          control.scope === sub
                            ? 'border-foreground bg-foreground'
                            : 'border-muted-foreground/50',
                        )}
                      />
                      {sub === 'workspace' ? 'Everyone in your workspace' : 'Only specific colleagues'}
                    </button>
                  ))}
                  </div>
                  {confirmWiden && (
                    <div className="rounded-md border border-amber-500/30 bg-amber-500/10 p-2 space-y-1.5">
                      <p className="text-xs text-amber-700 dark:text-amber-200">
                        Sharing with everyone removes{' '}
                        {namesAtStake === 1
                          ? 'the colleague you named'
                          : `the ${namesAtStake} colleagues you named`}
                        . They stay able to open it — as part of the workspace.
                      </p>
                      <div className="flex items-center gap-1.5">
                        <Button
                          variant="outline"
                          size="xs"
                          onClick={() => {
                            setConfirmWiden(false);
                            void control.chooseScope('workspace');
                          }}
                        >
                          Share with everyone
                        </Button>
                        <Button variant="ghost" size="xs" onClick={() => setConfirmWiden(false)}>
                          Keep the list
                        </Button>
                      </div>
                    </div>
                  )}
                  {/* FRONT-1926: "Only you" paused this list rather than
                      deleting it — offer it back rather than making the
                      person retype every name, but never restore it without
                      this explicit click. */}
                  {offerRestore && (
                    <div className="rounded-md border border-border bg-muted/40 p-2 space-y-1.5">
                      <p className="text-xs text-muted-foreground">
                        Share again with the same{' '}
                        {pausedColleagues === 1 ? 'colleague' : `${pausedColleagues} colleagues`}
                        ?
                      </p>
                      <div className="flex items-center gap-1.5">
                        <Button
                          variant="outline"
                          size="xs"
                          disabled={busy}
                          onClick={() => void control.restorePausedShare()}
                        >
                          Share again
                        </Button>
                        <Button
                          variant="ghost"
                          size="xs"
                          disabled={busy}
                          onClick={() => setStartFresh(true)}
                        >
                          Start over
                        </Button>
                      </div>
                    </div>
                  )}
                  {control.scope === 'people' && !offerRestore && (
                    <div className="pt-1">
                      <SharePeopleInput
                        people={control.people}
                        onAdd={(entry) => void control.addPerson(entry)}
                        onRemove={(entry) => void control.removePerson(entry)}
                        ownDomain={control.ownDomain}
                        disabled={busy}
                        lastEntryLocked={LAST_PERSON_LOCKED}
                      />
                      {control.people.length === 0 && (
                        <p className="mt-1.5 text-xs text-muted-foreground">
                          Add at least one colleague. Until then the app stays as it is.
                        </p>
                      )}
                    </div>
                  )}
                </div>
  );
}

/**
 * The app's one link, its copy action, and the sentence saying who it opens
 * for. The same link for every audience — that is the point of collapsing the
 * old `?share=` token into the audience.
 */
function AppLinkRow({
  target,
  control,
}: {
  target: ShareTarget;
  control: ReturnType<typeof useShareControl>;
}) {
  return (
      <div className="border-t border-border pt-2.5 space-y-1.5 min-w-0">
        <p className="text-xs font-medium">Link to this app</p>
        <div className="flex items-center gap-1.5">
          <span className="min-w-0 flex-1 truncate rounded-md bg-muted px-2 py-1 font-mono text-xs text-muted-foreground">
            {target.url}
          </span>
          <Button
            variant="outline"
            size="xs"
            onClick={() => void control.copyLink()}
            aria-label="Copy the app's link"
            className="shrink-0 active:scale-[0.96]"
          >
            {control.copied ? <CheckIcon size={12} weight="bold" /> : <LinkSimpleIcon size={12} />}
            {control.copied ? 'Copied' : 'Copy'}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">{whoOpensTheLink(target)}</p>
      </div>
  );
}

/**
 * The audience of an Apps ROW — the one derivation both the list and the
 * editor use (FRONT-1880).
 *
 * It existed twice, hand-copied, the moment named sharing arrived: the list
 * badge and the Sharing tab each computed "can a colleague open this" from
 * the columns, and the second one to learn about the named list would have
 * disagreed with the first until somebody noticed a badge saying Only you
 * about an app two people could open. Mirrors `sharedWithAnyColleague`
 * (`src/apps/access.ts`) — a null owner, the workspace flag, or a non-empty
 * named list.
 */
export function appRowAudience(row: {
  slug: string;
  status: string;
  visibility: string;
  /**
   * Whether any colleague can open it, as the SERVER computed it from the
   * full row (`serializeApp`). Never re-derived here from `workspaceShared`
   * and `shareAllowedEmails`: the named list is withheld from everyone but
   * the owner, so a client-side derivation badged the very colleague an app
   * was shared with "Only you" on their own Apps list — the same
   * one-surface-lies shape this whole control exists to remove. The coding
   * header's app view carries the identical boolean for the identical reason.
   */
  colleaguesCanOpen: boolean;
}): AppAudience {
  return appAudience({
    slug: row.slug,
    status: row.status,
    visibility: row.visibility,
    colleaguesCanOpen: row.colleaguesCanOpen,
    url: `/apps/${row.slug}/`,
  });
}

/**
 * What the app's own link does, in one sentence — the same link for every
 * audience, which is the point of collapsing the old `?share=` token into
 * this. Exported for its own test: it is the sentence that would silently
 * start lying if a sub-choice were added without touching it.
 */
export function whoOpensTheLink(
  target: Pick<ShareTarget, 'audience' | 'scope' | 'people' | 'live'>,
): string {
  // FRONT-2091: an offline app is served to its OWNER alone
  // (`resolveServableApp`, `src/apps/servable.ts`), so every sentence in the
  // live branch below would be a present-tense claim about a colleague who
  // gets a 404 today. Until this ticket the audience itself read "Only you"
  // whenever the app was offline, and this function was right by accident;
  // now the audience states the sharing decision and the lifecycle half has
  // to be said out loud. Same "once ... live" shape as
  // `describeDormantAudience` (`edit-app.tsx`), which says it for the
  // Sharing tab's note.
  if (!target.live) return offlineLinkSentence(target);
  if (target.audience === 'public') return 'Anyone with this link can open it.';
  if (target.audience === 'only-you') return 'Only you can open this link.';
  if (target.scope === 'people') {
    // FRONT-2756: only email entries count. A bare word saved earlier opens
    // the app to nobody, so "the one person you named" was a false promise.
    const n = target.people.filter(isValidEntry).length;
    if (n === 0) return 'Nobody can open this link yet — add someone above.';
    return n === 1
      ? 'The one person you named can open this link, once signed in.'
      : `The ${n} people you named can open this link, once signed in.`;
  }
  return 'Everyone in your workspace can open this link.';
}

/**
 * The same sentence for an app nobody but its owner can reach yet
 * (FRONT-2091): what the link does NOW, then what the stored sharing
 * decision will do once the app is live.
 *
 * Kept as its own function rather than as clauses spliced into the live
 * sentences — those end in three different ways (one carries its own ",
 * once signed in" tail), and gluing "once it's live" onto them produced
 * "…can open this link, once signed in once it's live."
 *
 * ponytail: `live` is a boolean, so two states this sentence cannot tell
 * apart read imprecisely. Both were raised by the FRONT-2091 review and both
 * are deliberate, not missed:
 *
 *  - `status === 'archived'` gets "once it's live", a promise nothing can
 *    keep — `resolveServableApp` serves an archived app to NOBODY, owner
 *    included, and `audienceLockReason` already refuses every audience
 *    change on one. No route, store function or control in this repo writes
 *    that status (grep `'archived'` under `src/apps`, `src/admin`,
 *    `src/coding`): it is a declared-but-unproduced value reachable only by
 *    hand-editing the row, which is the same ceiling `appAudienceFrom`'s own
 *    docblock has named since FRONT-1879. Widening `live` to a three-state
 *    lifecycle to serve it would touch all five mounts for a state the
 *    product cannot reach.
 *  - a LIVE app with nothing saved is equally unreachable
 *    (`hasRenderableContent`), and reads as live here. The Apps row says so
 *    with its own "No content" badge; this popover does not carry one.
 *
 * `describeDormantAudience` (`edit-app.tsx`, FRONT-2078) has the identical
 * archived behaviour, so these two stay consistent — fix one, fix both.
 */
function offlineLinkSentence(
  target: Pick<ShareTarget, 'audience' | 'scope' | 'people'>,
): string {
  // True offline as well as live, and nothing is waiting to happen — so it
  // gets no "once it's live" promise of a change that never comes.
  if (target.audience === 'only-you') return 'Only you can open this link.';
  const OFFLINE = "It's offline, so only you can open this link.";
  if (target.audience === 'public') {
    return `${OFFLINE} Anyone with the link will be able to open it once it's live.`;
  }
  if (target.scope === 'people') {
    // Only email entries count (FRONT-2756), same as the live sentence.
    const n = target.people.filter(isValidEntry).length;
    // Nothing has been decided yet, so there is nothing dormant to promise.
    if (n === 0) return 'Nobody can open this link yet — add someone above.';
    return n === 1
      ? `${OFFLINE} The one person you named will be able to open it once it's live.`
      : `${OFFLINE} The ${n} people you named will be able to open it once it's live.`;
  }
  return `${OFFLINE} Everyone in your workspace will be able to open it once it's live.`;
}
